import React, { FC, KeyboardEvent } from 'react';
import PortalHeader from '../../assessment-portal/components/portal-header';
import {
  MOBILE_PRIMARY_CTA_CLASS,
  MOBILE_PRIMARY_CTA_DISABLED_CLASS,
  MOBILE_PRIMARY_CTA_ENABLED_CLASS,
} from '../../assessment-portal/components/primary-cta';
import { YOUNG_QUESTIONNAIRE_MAX_AGE, YOUNG_QUESTIONNAIRE_MIN_AGE } from '../data/constants';
import type { QuestionnaireVariant } from '../utils/questionnaire-variant';

interface AgeRangePageProps {
  selected: QuestionnaireVariant | null;
  onSelect: (variant: QuestionnaireVariant) => void;
  onContinue: () => void;
  /** Date of birth puts the patient under the youngest supported age. */
  isUnderMinAge: boolean;
  /** Lets the user pick the age range themselves when the stored date of birth is wrong. */
  onChooseManually: () => void;
  showBack: boolean;
  onBack?: () => void;
}

interface AgeRangeOption {
  variant: QuestionnaireVariant;
  title: string;
  description: string;
}

const AGE_RANGE_OPTIONS: AgeRangeOption[] = [
  {
    variant: 'young',
    title: `${YOUNG_QUESTIONNAIRE_MIN_AGE} to ${YOUNG_QUESTIONNAIRE_MAX_AGE} years old`,
    description: 'A shorter set of questions written for young people, completed with a parent or carer.',
  },
  {
    variant: 'adult',
    title: `${YOUNG_QUESTIONNAIRE_MAX_AGE + 1} and over`,
    description: 'The standard questionnaire.',
  },
];

const PersonIcon: FC = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.8 20c0-3.8 3.2-6.2 7.2-6.2S19.2 16.2 19.2 20" />
  </svg>
);

const BackButton: FC<{ onBack: () => void }> = ({ onBack }) => (
  <button type="button" onClick={onBack} className="adhd-primer-back">
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M19 12H5M11 18l-6-6 6-6" />
    </svg>
    Back
  </button>
);

const ARROW_KEY_STEPS: Record<string, number> = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
};

const AgeRangeOptions: FC<Pick<AgeRangePageProps, 'selected' | 'onSelect'>> = ({
  selected,
  onSelect,
}) => {
  // Radiogroup keyboard pattern: one tab stop, arrow keys move the selection.
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = ARROW_KEY_STEPS[event.key];
    if (!step) return;
    event.preventDefault();
    const count = AGE_RANGE_OPTIONS.length;
    const nextIndex = (index + step + count) % count;
    onSelect(AGE_RANGE_OPTIONS[nextIndex].variant);
    const group = event.currentTarget.parentElement;
    group?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[nextIndex]?.focus();
  };

  return (
    <div
      className="adhd-age-options"
      role="radiogroup"
      aria-label="Age of the person this appointment is for"
    >
      {AGE_RANGE_OPTIONS.map((option, index) => {
        const isSelected = selected === option.variant;
        const isTabStop = isSelected || (selected === null && index === 0);
        return (
          <button
            key={option.variant}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isTabStop ? 0 : -1}
            onClick={() => onSelect(option.variant)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`adhd-age-option${isSelected ? ' adhd-age-option--selected' : ''}`}
          >
            <span className="adhd-age-radio" aria-hidden />
            <span>
              <span className="adhd-age-option-title">{option.title}</span>
              <span className="adhd-age-option-desc">{option.description}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

const UNDER_MIN_TITLE = 'We’re sorry';
const UNDER_MIN_MESSAGE = `This assessment isn’t currently available for children under ${YOUNG_QUESTIONNAIRE_MIN_AGE}.`;
const CHOOSE_MANUALLY_LABEL = 'Not this age? Choose manually';
const TITLE = 'Before you start';
const SUBTITLE = 'So we give you the right questions, tell us the age of the person this appointment is for.';

const AgeRangePage: FC<AgeRangePageProps> = ({
  selected,
  onSelect,
  onContinue,
  isUnderMinAge,
  onChooseManually,
  showBack,
  onBack,
}) => {
  const canContinue = !isUnderMinAge && selected !== null;
  const title = isUnderMinAge ? UNDER_MIN_TITLE : TITLE;
  const subtitle = isUnderMinAge ? UNDER_MIN_MESSAGE : SUBTITLE;
  const back = showBack && onBack ? onBack : undefined;

  return (
    <div className="adhd-portal adhd-primer-page adhd-age-page min-h-screen flex flex-col bg-page">
      <PortalHeader
        title={TITLE}
        subtitle="Pre-appointment questions"
        progress={100}
        onBack={back}
      />
      <main className="flex-1 flex items-start justify-center px-4 py-6 md:py-8">
        <div className="adhd-primer-card hidden md:block">
          <div className="adhd-primer-kicker"><PersonIcon /></div>
          <h1 className="adhd-primer-title">{title}</h1>
          <p className="adhd-primer-sub">{subtitle}</p>
          {!isUnderMinAge && <AgeRangeOptions selected={selected} onSelect={onSelect} />}
          {isUnderMinAge && (
            <button type="button" onClick={onChooseManually} className="adhd-age-manual">
              {CHOOSE_MANUALLY_LABEL}
            </button>
          )}
          <div className="adhd-primer-actions">
            {back && <BackButton onBack={back} />}
            {!isUnderMinAge && (
              <button
                type="button"
                onClick={onContinue}
                disabled={!canContinue}
                className="adhd-primer-start"
              >
                Continue
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="adhd-primer-mobile md:hidden">
          <h1 className="adhd-primer-mobile-title">{title}</h1>
          <p className="adhd-primer-mobile-lede">{subtitle}</p>
          {!isUnderMinAge && <AgeRangeOptions selected={selected} onSelect={onSelect} />}
          {isUnderMinAge && (
            <button type="button" onClick={onChooseManually} className="adhd-age-manual">
              {CHOOSE_MANUALLY_LABEL}
            </button>
          )}
        </div>
      </main>

      {!isUnderMinAge && (
        <>
          <div className="portal-navrow-spacer md:hidden" aria-hidden />
          <div className="portal-navrow--sticky md:hidden">
            <button
              type="button"
              onClick={onContinue}
              disabled={!canContinue}
              className={`${MOBILE_PRIMARY_CTA_CLASS} ${
                canContinue ? MOBILE_PRIMARY_CTA_ENABLED_CLASS : MOBILE_PRIMARY_CTA_DISABLED_CLASS
              }`}
            >
              Continue
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AgeRangePage;
